import { adminFetch } from "./_client";
import type { AdminTrip, TripDetailData, RefundComponentBreakdown, RefundPreviewResult, TripExtensionResponse, TripNote, TripEvent, TripMessagesResponse, TripNotificationsResponse } from "./types";


export type FunnelRange = "week" | "month" | "year";

// Task #6080 — refundable vs non-refundable rate-plan split over NATIVE
// bookings in the funnel window. `unknown` counts native rows with no
// rate_plan; null means the server couldn't compute the split.
export type RatePlanSplit = { flexible: number; non_refundable: number; unknown: number };

export function fetchAdminTrips(
  token: string,
  status = "",
  page = 1,
  search = "",
  occasion = "",
  funnelRange: FunnelRange = "year",
  // Task #6278 — optional created/event date-range filter (YYYY-MM-DD bounds).
  dateFilter?: { field: "created" | "event"; from?: string; to?: string },
  // Task #7255 — optional server-side sort for the trips list.
  sortField?: string | null,
  sortDir?: "asc" | "desc" | null,
) {
  const params = new URLSearchParams({ page: String(page) });
  if (status) params.set("status", status);
  if (search) params.set("search", search);
  if (occasion) params.set("occasion", occasion);
  params.set("funnelRange", funnelRange);
  if (dateFilter && (dateFilter.from || dateFilter.to)) {
    params.set("dateField", dateFilter.field);
    if (dateFilter.from) params.set("dateFrom", dateFilter.from);
    if (dateFilter.to) params.set("dateTo", dateFilter.to);
  }
  if (sortField) params.set("sort_field", sortField);
  if (sortDir) params.set("sort_dir", sortDir);
  return adminFetch<{ trips: AdminTrip[]; total: number; page: number; limit: number; statusCounts?: Record<string, number>; funnelRange?: FunnelRange; ratePlanSplit?: RatePlanSplit | null }>(`/admin/trips?${params}`, token);
}


export function fetchAdminTripsForCar(token: string, carId: number, page = 1) {
  const params = new URLSearchParams({ page: String(page), carId: String(carId) });
  return adminFetch<{ trips: AdminTrip[]; total: number; page: number; limit: number }>(`/admin/trips?${params}`, token);
}


export function fetchAdminTrip(token: string, id: number) {
  return adminFetch<TripDetailData>(`/admin/trips/${id}`, token);
}


// ── Task #6975 — trip safety-location stamps + wellness checks ─────────────

export interface TripLocationStamp {
  id: number;
  moment: "check_in" | "mid_trip" | "check_out" | "wellness_response" | string;
  source: "gps" | "ip" | string;
  lat: number | null;
  lng: number | null;
  accuracy_m: number | null;
  place_label: string | null;
  created_at: string;
}

export interface TripWellnessCheck {
  id: number;
  status: "pending" | "ok" | "needs_help" | "no_response" | string;
  triggered_by: number | null;
  responded_at: string | null;
  stamp_id: number | null;
  expires_at: string;
  created_at: string;
}

export function fetchTripLocationStamps(token: string, tripId: number) {
  return adminFetch<{ stamps: TripLocationStamp[]; checks: TripWellnessCheck[] }>(
    `/admin/trips/${tripId}/location-stamps`,
    token,
  );
}

export function triggerTripWellnessCheck(token: string, tripId: number) {
  return adminFetch<{ check: TripWellnessCheck }>(
    `/admin/trips/${tripId}/wellness-check`,
    token,
    { method: "POST" },
  );
}


// ── Self-drive background route tracking (ops/admin-only) ──────────────────
//
// Background GPS route points captured by the renter's app during an active
// native self-drive trip (see trip_track_points). Visible to ops/admin ONLY
// (hosts never see it); the backend audit-logs every view. Points are purged
// 30 days after the trip closes.

export interface TripTrackPoint {
  id: number;
  lat: number;
  lng: number;
  accuracy_m: number | null;
  speed_mps: number | null;
  heading_deg: number | null;
  recorded_at: string;
}

export function fetchTripTrackPoints(token: string, tripId: number) {
  return adminFetch<{
    points: TripTrackPoint[];
    /** Set when the renter explicitly declined the OS location permission
     * for the track-by-default flow — the trip is knowingly untracked. */
    tracking_declined_at?: string | null;
  }>(`/admin/trips/${tripId}/track-points`, token);
}


// Task #2728 — admin actions for the fraud-review banner on the trip
// detail page. Both require an admin note that gets persisted as a trip
// note and an audit_log row (action: fraud_review_cleared /
// fraud_review_cancelled_refunded) so the Activity tab reflects them.
export function clearTripFraudReview(token: string, id: number, note: string) {
  return adminFetch<{ success: boolean }>(`/admin/trips/${id}/fraud/clear`, token, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}


export function cancelAndRefundTripFraud(token: string, id: number, note: string) {
  return adminFetch<{ success: boolean }>(`/admin/trips/${id}/fraud/cancel-refund`, token, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}


export function overrideTripStatus(token: string, id: number, status: string, reason: string) {
  return adminFetch<{ success: boolean }>(`/admin/trips/${id}/status`, token, {
    method: "POST",
    body: JSON.stringify({ status, reason }),
  });
}


export interface AdminRefundRequest {
  previewId: string;
  intent: "full" | "partial";
  amount: number;
  currency: string;
  reason: string;
}

export function refundTrip(token: string, id: number, request: AdminRefundRequest) {
  return adminFetch<{
    success: boolean; status: "succeeded" | "incomplete"; amount: number; requestedAmount: number;
    currency: string; attemptId: string; message: string;
    refunds: { id: string; charge: string; amount: number; status: string }[];
  }>(`/admin/trips/${id}/refund`, token, {
    method: "POST",
    body: JSON.stringify(request),
  });
}


export function previewTripRefund(
  token: string,
  id: number,
  body: { components?: Partial<RefundComponentBreakdown>; cancelledByOwner?: boolean } = {},
) {
  return adminFetch<RefundPreviewResult>(`/admin/trips/${id}/refund/preview`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}


export function sendTripAdminReply(
  token: string,
  id: number,
  body: { content: string; reason: string; asUserId?: string },
) {
  return adminFetch<{ ok: boolean; messageId: number; sentAs: string; createdAt: string }>(`/admin/trips/${id}/messages/reply`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}


export function fetchTripExtension(token: string, id: number) {
  return adminFetch<TripExtensionResponse>(`/admin/trips/${id}/extension`, token);
}


export function respondTripExtension(
  token: string,
  id: number,
  adjustmentId: number,
  action: "accept" | "decline",
  reason: string,
) {
  return adminFetch<{ ok: boolean; action: string; adjustmentId: number }>(`/admin/trips/${id}/extension/${adjustmentId}/respond`, token, {
    method: "POST",
    body: JSON.stringify({ action, reason }),
  });
}


export function nudgeTripExtension(token: string, id: number, adjustmentId: number) {
  return adminFetch<{ ok: boolean; responderId: string }>(`/admin/trips/${id}/extension/${adjustmentId}/nudge`, token, {
    method: "POST",
  });
}


export function addTripNote(token: string, id: number, note: string) {
  return adminFetch<TripNote>(`/admin/trips/${id}/notes`, token, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}


export function updateTripNote(token: string, id: number, noteId: number, note: string) {
  return adminFetch<TripNote>(`/admin/trips/${id}/notes/${noteId}`, token, {
    method: "PATCH",
    body: JSON.stringify({ note }),
  });
}


export function captureDeposit(token: string, id: number, amount?: number) {
  return adminFetch<{ success: boolean; capturedAmount: number; paymentIntentId: string }>(`/admin/trips/${id}/deposit-capture`, token, {
    method: "POST",
    body: JSON.stringify({ amount }),
  });
}


export function releaseDeposit(token: string, id: number) {
  return adminFetch<{ success: boolean }>(`/admin/trips/${id}/deposit-release`, token, {
    method: "POST",
  });
}


export function fetchTripEvents(token: string, id: number) {
  return adminFetch<{ events: TripEvent[] }>(`/admin/trips/${id}/events`, token);
}


export function fetchTripMessages(
  token: string,
  id: number,
  opts: { limit?: number; before?: string | null } = {},
) {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.before) params.set("before", opts.before);
  const qs = params.toString();
  return adminFetch<TripMessagesResponse>(`/admin/trips/${id}/messages${qs ? `?${qs}` : ""}`, token);
}


export function fetchTripNotifications(token: string, id: number) {
  return adminFetch<TripNotificationsResponse>(`/admin/trips/${id}/notifications`, token);
}


// Re-send a previously-dispatched email notification verbatim (same content,
// same recipient as the original send). The server replays the persisted
// content snapshot through the normal email path and records a fresh ledger
// row + audit entry. `outcome` mirrors the send result so the UI can show
// whether it actually went out, was suppressed by the go-live gate, etc.
export function resendTripNotification(token: string, id: number, ledgerId: number) {
  return adminFetch<{
    ok: boolean;
    outcome: "sent" | "suppressed" | "failed" | "skipped";
    reason: string | null;
    providerMessageId: string | null;
    recipient: string;
  }>(`/admin/trips/${id}/notifications/${ledgerId}/resend`, token, {
    method: "POST",
  });
}


export function stopTripPayout(token: string, id: number, reason?: string) {
  return adminFetch<{ success: boolean }>(`/admin/trips/${id}/stop-payout`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}


export function resumeTripPayout(token: string, id: number, reason?: string) {
  return adminFetch<{ success: boolean }>(`/admin/trips/${id}/resume-payout`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}


export function createManualBilling(token: string, id: number, data: { amount: number; currency?: string; description?: string; type: "charge" | "credit" }) {
  return adminFetch<{ success: boolean; billing: Record<string, unknown> }>(`/admin/trips/${id}/manual-billing`, token, {
    method: "POST",
    body: JSON.stringify(data),
  });
}


export function editTripFields(token: string, id: number, fields: Record<string, unknown>) {
  return adminFetch<{ success: boolean }>(`/admin/trips/${id}/edit`, token, {
    method: "POST",
    body: JSON.stringify({ fields }),
  });
}


// Task #5955 — trip-scoped host-payout controls.
export interface TripPayoutContext {
  tripId: number;
  publicId: string;
  payoutStatus: number;
  currentlyStopped: boolean;
  alreadyPaid: boolean;
  // Task #6034 — imported trip stamped payout_status=2 by the legacy import /
  // bac-legacy-auto writer, but with NO settlement record (no op=3 billing row,
  // no completed payout link). Recording an external payout is still allowed.
  importStampedPaid: boolean;
  payoutAmount: number;
  currency: string;
  rail: "connect" | "manual_external";
  hasConnectAccount: boolean;
  region: string;
  approvalGateOn: boolean;
  legacy: {
    isLegacyCsvImport: boolean;
    isImported: boolean;
    isMigratedOrDs: boolean;
    balancePayoutAllowed: boolean;
    manualImportPayoutAllowed: boolean;
  };
  existingPayout: { id: number; amount: number; currency: string; external: boolean; reference: string | null; createdAt: string | null } | null;
}

export function fetchTripPayoutContext(token: string, id: number) {
  return adminFetch<TripPayoutContext>(`/admin/trips/${id}/payout`, token);
}

export function releaseTripPayoutNow(token: string, id: number, reason: string) {
  return adminFetch<{ ok: boolean; actionId: number | null; queued: boolean; approvalGateOn: boolean }>(`/admin/trips/${id}/payout/release-now`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function adjustTripPayout(token: string, id: number, amount: number, reason: string) {
  return adminFetch<{ ok: boolean; actionId: number | null; previousAmount: number; newAmount: number; delta: number; currency: string }>(`/admin/trips/${id}/payout/adjust`, token, {
    method: "POST",
    body: JSON.stringify({ amount, reason }),
  });
}

export function createExtraTripPayout(token: string, id: number, amount: number, reason: string) {
  return adminFetch<{ ok: boolean; actionId: number | null; transferId: string; billingId: number | null; amount: number; currency: string }>(`/admin/trips/${id}/payout/extra`, token, {
    method: "POST",
    body: JSON.stringify({ amount, reason }),
  });
}

// Pay an imported booking's host from the platform balance for an admin-entered
// amount (primary payout — imported trips have no accepted suggestion to derive
// it from). Native trips use releaseTripPayoutNow instead.
export function createManualImportTripPayout(token: string, id: number, amount: number, reason: string) {
  return adminFetch<{ ok: boolean; actionId: number | null; transferId: string; billingId: number | null; amount: number; currency: string }>(`/admin/trips/${id}/payout/manual`, token, {
    method: "POST",
    body: JSON.stringify({ amount, reason }),
  });
}

// Task #7746 — create a trip_suggestions pricing snapshot for a pending booking
// that is missing one, so the host can proceed with approving it. Only allowed
// when the booking has no existing suggestion row (admin unblock tool).
// payment_due is REQUIRED: it gates the zero-amount-approve path in the approve
// route — omitting it causes the route to read null→0 and confirm a paid booking
// without charging the renter.
export function createTripPricingSnapshot(
  token: string,
  id: number,
  data: { rental_fee: number; payment_due: number; service_fee?: number; delivery_fee?: number; owner_earn?: number },
) {
  return adminFetch<{ ok: boolean; suggestion_id: number | null }>(`/admin/trips/${id}/create-pricing-snapshot`, token, {
    method: "POST",
    body: JSON.stringify(data),
  });
}


// Record (NOT send) that an imported booking's host was paid off-platform (bank
// transfer, cash, legacy settlement). No Stripe money moves — this only logs the
// external payout so the trip stops showing as owed. Migrated / DS-origin trips
// with no Connect destination are settled this way.
export function recordExternalTripPayout(
  token: string,
  id: number,
  data: { amount: number; reason: string; reference?: string; currency?: string },
) {
  return adminFetch<{ ok: boolean; actionId: number | null; billingId: number | null; amount: number; currency: string; reference: string | null }>(`/admin/trips/${id}/payout/record-external`, token, {
    method: "POST",
    body: JSON.stringify(data),
  });
}
